import { CATEGORY_SHORT, UNKNOWN_CATEGORY_SHORT } from "../deviceCategories";
import type { BuildCircuitNames, CircuitName, CircuitNameInput } from "./types";

const detailOf = (typeCounts: Record<string, number>, moreTypes: (count: number) => string): string => {
  const types = Object.entries(typeCounts)
    .filter(([type]) => type)
    .sort(([a, countA], [b, countB]) => countB - countA || a.localeCompare(b));
  if (!types.length) return "";
  const [[type, count]] = types;
  return types.length > 1 ? `${count} × ${type} ${moreTypes(types.length - 1)}` : `${count} × ${type}`;
};

const prefixOf = (circuit: CircuitNameInput): string =>
  circuit.category ? CATEGORY_SHORT[circuit.category] : UNKNOWN_CATEGORY_SHORT;

export const buildCircuitNames: BuildCircuitNames = ({ circuits, custom, moreTypes }) => {
  const names: Record<string, CircuitName> = {};
  // Numbers his created circuits already use are not handed out again on the same board.
  const used = new Set<string>();
  circuits.forEach((circuit) => {
    if (circuit.createdKey) used.add(`${circuit.boardLabel}|${circuit.createdKey}`);
  });

  // Ordered by id so the same circuits get the same numbers on every render.
  const needsNumber = [...circuits]
    .filter((circuit) => !custom[circuit.id]?.trim() && !circuit.printedKey && !circuit.createdKey)
    .sort((a, b) => a.id.localeCompare(b.id));
  const numbered: Record<string, string> = {};
  needsNumber.forEach((circuit) => {
    const prefix = prefixOf(circuit);
    let n = 1;
    while (used.has(`${circuit.boardLabel}|${prefix}${n}`)) n += 1;
    used.add(`${circuit.boardLabel}|${prefix}${n}`);
    numbered[circuit.id] = `${prefix}${n}`;
  });

  circuits.forEach((circuit) => {
    const detail = detailOf(circuit.typeCounts, moreTypes);
    const yours = custom[circuit.id]?.trim();
    const onBoard = (key: string) => (circuit.boardLabel ? `${circuit.boardName} · ${key}` : key);
    if (yours) names[circuit.id] = { name: yours, detail, from: "yours" };
    else if (circuit.printedKey) names[circuit.id] = { name: circuit.printedKey, detail, from: "plan" };
    else if (circuit.createdKey) names[circuit.id] = { name: onBoard(circuit.createdKey), detail, from: "created" };
    else names[circuit.id] = { name: onBoard(numbered[circuit.id]), detail, from: "app" };
  });
  return names;
};
